/**
 * RFQContext
 *
 * Shared state for all RFQ sub-pages (Detail, Products, Suppliers,
 * Quotes Review). Fetches rfqData + products once and caches them
 * across sub-page navigations.
 *
 * ## Refresh Usage Rules (MUST follow)
 * - refreshRFQ()      → metadata-only changes (phase, title, delete)
 * - refreshProducts() → product mutations (edit, category change)
 * - refreshAll()      → ONLY initial load or hard reset (e.g. RFQ split)
 *
 * ## Loading Behaviour on requestId Change
 * When requestId changes, stale data is cleared IMMEDIATELY before
 * the new fetch begins — this prevents RFQ A from flashing inside
 * RFQ B during transitions.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { API_ENDPOINTS } from '../utils/config';
import { useAuth } from '../context/AuthContext';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface RFQContextType {
  /** The active RFQ request ID extracted from the route. */
  requestId: string;

  /** Full RFQ metadata from REQUEST_V2_DETAIL. `null` while loading. */
  rfqData: any | null;

  /** Products array from REQUEST_V2_PRODUCTS. Empty while loading. */
  products: any[];

  /** `true` during the initial fetch or any refresh. */
  isLoading: boolean;

  /** Error message if the most recent fetch failed. */
  error: string | null;

  /**
   * Derived safe accessor — always returns a displayable RFQ identifier.
   * Falls back to the last 6 chars of the raw requestId when rfqData
   * hasn't loaded yet.
   */
  companyRequestId: string;

  /**
   * Re-fetch RFQ metadata only.
   * Use after: phase change, title edit, delete.
   */
  refreshRFQ: () => Promise<void>;

  /**
   * Re-fetch products only.
   * Use after: product edit/save, category change.
   */
  refreshProducts: () => Promise<void>;

  /**
   * Re-fetch both rfqData AND products.
   * Use ONLY for: initial load or hard-reset scenarios (e.g. RFQ split
   * where both metadata and product list change).
   */
  refreshAll: () => Promise<void>;
}

/* ------------------------------------------------------------------ */
/*  Context + sentinel                                                 */
/* ------------------------------------------------------------------ */

const RFQContext = createContext<RFQContextType | null>(null);

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */

interface RFQProviderProps {
  requestId: string;
  children: ReactNode;
}

export const RFQProvider: React.FC<RFQProviderProps> = ({
  requestId,
  children,
}) => {
  const { token } = useAuth();
  const [rfqData, setRfqData] = useState<any | null>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Guard against stale responses when requestId changes mid-flight.
  const activeRequestRef = useRef(requestId);

  /* ---------- fetch helpers ---------- */

  const fetchRFQ = useCallback(async (): Promise<any | null> => {
    if (!token) return null;
    const res = await fetch(API_ENDPOINTS.REQUEST_V2_DETAIL(requestId), {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`RFQ fetch failed (${res.status})`);
    const json = await res.json();
    if (!json?.success || !json?.data) throw new Error('Invalid RFQ response');
    return json.data;
  }, [requestId, token]);

  const fetchProducts = useCallback(async (): Promise<any[]> => {
    if (!token) return [];
    const res = await fetch(API_ENDPOINTS.REQUEST_V2_PRODUCTS(requestId), {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`Products fetch failed (${res.status})`);
    const json = await res.json();
    if (!json?.success) throw new Error('Invalid products response');
    return json.data ?? [];
  }, [requestId, token]);

  /* ---------- refresh actions ---------- */

  const refreshRFQ = useCallback(async () => {
    try {
      const data = await fetchRFQ();
      if (activeRequestRef.current === requestId) {
        setRfqData(data);
      }
    } catch (err: any) {
      console.error('[RFQContext] refreshRFQ failed:', err);
      if (activeRequestRef.current === requestId) {
        setError(err?.message || 'Failed to refresh RFQ');
      }
    }
  }, [fetchRFQ, requestId]);

  const refreshProducts = useCallback(async () => {
    try {
      const data = await fetchProducts();
      if (activeRequestRef.current === requestId) {
        setProducts(data);
      }
    } catch (err: any) {
      console.error('[RFQContext] refreshProducts failed:', err);
      if (activeRequestRef.current === requestId) {
        setError(err?.message || 'Failed to refresh products');
      }
    }
  }, [fetchProducts, requestId]);

  const refreshAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [rfq, prods] = await Promise.all([fetchRFQ(), fetchProducts()]);
      if (activeRequestRef.current === requestId) {
        setRfqData(rfq);
        setProducts(prods);
      }
    } catch (err: any) {
      console.error('[RFQContext] refreshAll failed:', err);
      if (activeRequestRef.current === requestId) {
        setError(err?.message || 'Failed to load RFQ data');
      }
    } finally {
      if (activeRequestRef.current === requestId) {
        setIsLoading(false);
      }
    }
  }, [fetchRFQ, fetchProducts, requestId]);

  /* ---------- initial load + requestId change ---------- */

  useEffect(() => {
    // MUST FIX 2: Clear stale data immediately to prevent RFQ A
    // from flashing inside RFQ B during transition.
    activeRequestRef.current = requestId;
    setRfqData(null);
    setProducts([]);
    setError(null);
    setIsLoading(true);

    if (!requestId || !token) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    Promise.all([fetchRFQ(), fetchProducts()])
      .then(([rfq, prods]) => {
        if (cancelled) return;
        setRfqData(rfq);
        setProducts(prods);
      })
      .catch((err: any) => {
        if (cancelled) return;
        console.error('[RFQContext] initial fetch failed:', err);
        setError(err?.message || 'Failed to load RFQ data');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [requestId, token, fetchRFQ, fetchProducts]);

  /* ---------- derived fields ---------- */

  // MUST FIX 3: Safe accessor that never throws on null rfqData.
  const companyRequestId = rfqData?.company_request_id ?? requestId.slice(-6);

  /* ---------- memoised value ---------- */

  const value = useMemo<RFQContextType>(
    () => ({
      requestId,
      rfqData,
      products,
      isLoading,
      error,
      companyRequestId,
      refreshRFQ,
      refreshProducts,
      refreshAll,
    }),
    [
      requestId,
      rfqData,
      products,
      isLoading,
      error,
      companyRequestId,
      refreshRFQ,
      refreshProducts,
      refreshAll,
    ],
  );

  return <RFQContext.Provider value={value}>{children}</RFQContext.Provider>;
};

/* ------------------------------------------------------------------ */
/*  Consumer hooks                                                     */
/* ------------------------------------------------------------------ */

/**
 * Full context — throws if used outside `<RFQProvider>`.
 */
export function useRFQContext(): RFQContextType {
  const ctx = useContext(RFQContext);
  if (!ctx) {
    throw new Error('useRFQContext must be used within an <RFQProvider>');
  }
  return ctx;
}

/**
 * Lightweight hook for components that only need the requestId.
 */
export function useRFQRequestId(): string {
  return useRFQContext().requestId;
}

/**
 * Optional context — returns `null` when outside a provider.
 * Useful for components that can render both inside and outside
 * RFQ routes (e.g. RFQContextBanner).
 */
export function useOptionalRFQContext(): RFQContextType | null {
  return useContext(RFQContext);
}
